import {
    AccountBalance,
    Delete,
    Edit,
    FileDownload,
    Search,
    TrendingDown,
    TrendingUp,
    WorkspacePremium,
} from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Link,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import React, { useCallback, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import 'chart.js/auto';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import CustomPagination from '../../components/Pagination';
import QuotaLimitedButton from '../../components/subscription/QuotaLimitedButton';
import PageTutorialButtons from '../../components/tutorials/PageTutorialButtons';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatCurrencyInCents, formatDate, formatUuid, presentUuid } from '../../helpers/formatters';
import { applyAuthCode } from '../../helpers/captureMode';
import {
    defaultOrdersDateRange,
    formatOrdersPeriodLabel,
    normalizeOrdersDateRange,
    ordersDateRangeForApi,
} from '../../helpers/orders';
import {
    downloadTransactionsReportCsv,
    fetchAllTransactionsForReport,
} from '../../helpers/transactionsReportCsv';
import { useListFetch } from '../../hooks/useListFetch';

function TransactionsPage() {
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSearchQuery, setActiveSearchQuery] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [exportingCsv, setExportingCsv] = useState(false);
    const [reconciliationData, setReconciliationData] = useState({
        totalIncome: 0,
        totalOutcome: 0,
        balance: 0,
        transactionCount: 0,
        incomeCount: 0,
        outcomeCount: 0,
        averageTransaction: 0,
        expenseTotal: 0,
        withdrawalTotal: 0,
        expenseCount: 0,
        withdrawalCount: 0,
        daily: [],
    });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const defaultDateRange = useMemo(() => defaultOrdersDateRange(), []);
    const [draftDateFrom, setDraftDateFrom] = useState(defaultDateRange.from);
    const [draftDateTo, setDraftDateTo] = useState(defaultDateRange.to);
    const [draftType, setDraftType] = useState('');
    const [filters, setFilters] = useState({
        from: defaultDateRange.from,
        to: defaultDateRange.to,
        type: '',
    });

    const fetchTransactions = useCallback(() => {
        setIsLoading(true);

        const listParams = new URLSearchParams({
            page: String(currentPage),
            limit: String(itemsPerPage),
        });
        const summaryParams = new URLSearchParams();
        if (activeSearchQuery) {
            listParams.set('q', activeSearchQuery);
            summaryParams.set('q', activeSearchQuery);
        }
        if (filters.from) {
            listParams.set('from', filters.from);
            summaryParams.set('from', filters.from);
        }
        if (filters.to) {
            listParams.set('to', filters.to);
            summaryParams.set('to', filters.to);
        }
        if (filters.type) {
            listParams.set('type', filters.type);
            summaryParams.set('type', filters.type);
        }

        const reconciliationQuery = summaryParams.toString();
        Promise.all([
            api.get(`transactions?${listParams.toString()}`),
            api.get(
                reconciliationQuery
                    ? `transactions/reconciliation?${reconciliationQuery}`
                    : 'transactions/reconciliation',
            ).catch(() => ({ data: { success: false, data: {} } })),
        ])
            .then(([transactionsResponse, reconciliationResponse]) => {
                const payload = transactionsResponse.data?.data || {};
                const list = payload.transactions || [];
                setTransactions(list);
                setTotalPages(payload.total_pages || 1);

                if (reconciliationResponse?.data?.success) {
                    const backendData = reconciliationResponse.data.data || {};
                    setReconciliationData({
                        totalIncome: backendData.total_income || 0,
                        totalOutcome: backendData.total_outcome || 0,
                        balance: backendData.balance || 0,
                        transactionCount: backendData.transaction_count || 0,
                        incomeCount: backendData.income_count || 0,
                        outcomeCount: backendData.outcome_count || 0,
                        averageTransaction: backendData.average_transaction || 0,
                        expenseTotal: backendData.expense_total || 0,
                        withdrawalTotal: backendData.withdrawal_total || 0,
                        expenseCount: backendData.expense_count || 0,
                        withdrawalCount: backendData.withdrawal_count || 0,
                        daily: Array.isArray(backendData.daily) ? backendData.daily : [],
                    });
                } else {
                    setReconciliationData((prev) => ({ ...prev, daily: [] }));
                }
            })
            .catch(() => {
                setTransactions([]);
                setTotalPages(1);
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage, itemsPerPage, activeSearchQuery, filters.from, filters.to, filters.type]);

    useListFetch(fetchTransactions, [
        currentPage,
        itemsPerPage,
        activeSearchQuery,
        filters.from,
        filters.to,
        filters.type,
    ]);

    const handleSearch = () => {
        setCurrentPage(1);
        setActiveSearchQuery(searchTerm.trim());
        setAppliedSearchTerm(Boolean(searchTerm.trim()));
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setActiveSearchQuery('');
        setCurrentPage(1);
        setAppliedSearchTerm(false);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleDraftDateFromChange = (value) => {
        setDraftDateFrom(value);
        if (!value) {
            return;
        }
        const normalized = normalizeOrdersDateRange(value, draftDateTo || value);
        setDraftDateFrom(normalized.from);
        setDraftDateTo(normalized.to);
    };

    const handleDraftDateToChange = (value) => {
        setDraftDateTo(value);
        if (!value || !draftDateFrom) {
            return;
        }
        const normalized = normalizeOrdersDateRange(draftDateFrom, value);
        setDraftDateFrom(normalized.from);
        setDraftDateTo(normalized.to);
    };

    const handleApplyFilters = () => {
        const range = ordersDateRangeForApi(draftDateFrom, draftDateTo);
        if (range.error) {
            Swal.fire({ icon: 'warning', title: 'Período inválido', text: range.error });
            return;
        }
        setFilters({
            from: range.from,
            to: range.to,
            type: draftType || '',
        });
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        const next = defaultOrdersDateRange();
        setDraftDateFrom(next.from);
        setDraftDateTo(next.to);
        setDraftType('');
        setFilters({
            from: next.from,
            to: next.to,
            type: '',
        });
        setCurrentPage(1);
    };

    const handleExportCsv = async () => {
        if (exportingCsv) {
            return;
        }
        setExportingCsv(true);
        try {
            const allTransactions = await fetchAllTransactionsForReport(api, {
                q: activeSearchQuery,
                from: filters.from,
                to: filters.to,
                type: filters.type,
            });
            if (!allTransactions.length) {
                await Swal.fire({
                    icon: 'info',
                    title: 'Nenhuma transação',
                    text: 'Não há transações no período/filtros atuais para exportar.',
                });
                return;
            }
            const stamp = `${filters.from || 'inicio'}_${filters.to || 'fim'}`;
            downloadTransactionsReportCsv(allTransactions, `relatorio-transacoes-${stamp}.csv`);
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Falha ao exportar',
                text: 'Não foi possível gerar o relatório CSV. Tente novamente.',
            });
        } finally {
            setExportingCsv(false);
        }
    };

    const periodLabel = formatOrdersPeriodLabel(filters.from, filters.to);
    const typeLabel = filters.type === 'income'
        ? 'Entradas'
        : filters.type === 'outcome'
            ? 'Saídas'
            : '';

    const dailySeries = reconciliationData.daily || [];
    const dailyChartData = useMemo(() => ({
        labels: dailySeries.map((point) => {
            const raw = String(point.date || '');
            const parts = raw.split('-');
            if (parts.length === 3) {
                return `${parts[2]}/${parts[1]}`;
            }
            return raw;
        }),
        datasets: [
            {
                label: 'Entradas',
                data: dailySeries.map((point) => (Number(point.income_total) || 0) / 100),
                borderColor: '#2e7d32',
                backgroundColor: '#2e7d32',
                tension: 0.2,
                pointRadius: dailySeries.length > 45 ? 0 : 3,
            },
            {
                label: 'Saídas',
                data: dailySeries.map((point) => (Number(point.outcome_total) || 0) / 100),
                borderColor: '#d32f2f',
                backgroundColor: '#d32f2f',
                tension: 0.2,
                pointRadius: dailySeries.length > 45 ? 0 : 3,
            },
            {
                label: 'Retiradas',
                data: dailySeries.map((point) => (Number(point.withdrawal_total) || 0) / 100),
                borderColor: '#1976d2',
                backgroundColor: '#1976d2',
                tension: 0.2,
                pointRadius: dailySeries.length > 45 ? 0 : 3,
            },
        ],
    }), [dailySeries]);

    const dailyChartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        scales: {
            x: {
                ticks: {
                    maxRotation: 45,
                    minRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: isMobile ? 8 : 16,
                },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    callback(value) {
                        return `R$ ${Number(value).toFixed(2).replace('.', ',')}`;
                    },
                },
            },
        },
        plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    label(context) {
                        const value = Number(context.parsed.y) || 0;
                        return `${context.dataset.label}: R$ ${value.toFixed(2).replace('.', ',')}`;
                    },
                },
            },
        },
    }), [isMobile]);

    const getTransactionTypeLabel = (type) => {
        switch (type) {
            case 'income':
                return 'Entrada';
            case 'outcome':
                return 'Saída';
            default:
                return type;
        }
    };

    const getOutcomeCategoryLabel = (category) => {
        switch (category) {
            case 'withdrawal':
                return 'Retirada';
            case 'expense':
                return 'Despesa';
            default:
                return '';
        }
    };

    const getPaymentMethodLabel = (method) => {
        switch (method) {
            case 'credit_card':
                return 'Cartão de Crédito';
            case 'debit_card':
                return 'Cartão de Débito';
            case 'pix':
                return 'PIX';
            case 'bank_transfer':
                return 'Transferência Bancária';
            case 'cash':
                return 'Dinheiro';
            case 'other':
                return 'Outro';
            default:
                return method;
        }
    };

    const getTransactionTypeColor = (type) => {
        switch (type) {
            case 'income':
                return 'success.main';
            case 'outcome':
                return 'error.main';
            default:
                return 'text.primary';
        }
    };

    const handleEditTransaction = (transactionId) => {
        navigate(`/admin/edit-transaction/${transactionId}`);
    };

    const handleDeleteTransaction = (transactionId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Esta transação será removida permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete,
            preConfirm: () => {
                if (!loadingDelete) {
                    setLoadingDelete(true);
                    return api.delete(`transactions/${transactionId}`)
                        .then(() => {
                            setLoadingDelete(false);
                            setTransactions(transactions.filter((transaction) => transaction.id !== transactionId));
                            Swal.fire('Removido!', 'A transação foi removida com sucesso.', 'success');
                        })
                        .catch(() => {
                            setLoadingDelete(false);
                            Swal.fire('Erro!', 'Houve um problema ao remover a transação.', 'error');
                        });
                }
            },
        });
    };

    if (isLoading) {
        return <Loading />;
    }
    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Typography color="text.primary">Transações</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: isMobile ? 2 : 0,
            }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h4">
                        Transações
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        gap: 2,
                        width: isMobile ? '100%' : 'auto',
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar transação..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth={isMobile}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                        fullWidth={isMobile}
                    >
                        Buscar
                    </Button>
                    {appliedSearchTerm && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearSearch}
                            startIcon={<ClearIcon />}
                            fullWidth={isMobile}
                        >
                            Limpar Busca
                        </Button>
                    )}
                    <PageTutorialButtons pageKey="transactions" fullWidth={isMobile} />
                    <QuotaLimitedButton
                        quotaKey="transactions"
                        variant="contained"
                        color="success"
                        onClick={() => navigate('/admin/create-transaction')}
                        fullWidth={isMobile}
                        sx={{
                            backgroundColor: '#4caf50',
                            '&:hover': {
                                backgroundColor: '#45a049',
                            },
                        }}
                    >
                        + Nova Transação
                    </QuotaLimitedButton>
                </Box>
            </Box>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Filtros das transações
                </Typography>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data inicial"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={draftDateFrom}
                            onChange={(e) => handleDraftDateFromChange(e.target.value)}
                            inputProps={{ max: draftDateTo || undefined }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data final"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={draftDateTo}
                            onChange={(e) => handleDraftDateToChange(e.target.value)}
                            inputProps={{
                                min: draftDateFrom || undefined,
                            }}
                            disabled={!draftDateFrom}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            select
                            label="Tipo"
                            size="small"
                            fullWidth
                            value={draftType}
                            onChange={(e) => setDraftType(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="income">Entrada</MenuItem>
                            <MenuItem value="outcome">Saída</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            <Button variant="contained" onClick={handleApplyFilters}>
                                Aplicar filtros
                            </Button>
                            <Button variant="outlined" onClick={handleResetFilters}>
                                Mês atual
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleExportCsv}
                                disabled={exportingCsv}
                                startIcon={exportingCsv ? <CircularProgress size={16} /> : <FileDownload />}
                            >
                                {exportingCsv ? 'Exportando…' : 'Exportar CSV'}
                            </Button>
                            {periodLabel && (
                                <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    label={periodLabel}
                                />
                            )}
                            {typeLabel && (
                                <Chip
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    label={typeLabel}
                                />
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TrendingUp color="success" sx={{ mr: 1 }} />
                                <Typography variant="h6" color="success.main">
                                    Entradas
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                                {formatCurrencyInCents(reconciliationData.totalIncome)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {reconciliationData.incomeCount}
                                {' '}
                                transação(ões)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TrendingDown color="error" sx={{ mr: 1 }} />
                                <Typography variant="h6" color="error.main">
                                    Saídas
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="error.main" sx={{ fontWeight: 'bold' }}>
                                {formatCurrencyInCents(reconciliationData.totalOutcome)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {reconciliationData.outcomeCount}
                                {' '}
                                transação(ões)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                Despesas:
                                {' '}
                                {formatCurrencyInCents(reconciliationData.expenseTotal)}
                                {reconciliationData.expenseCount > 0
                                    ? ` (${reconciliationData.expenseCount})`
                                    : ''}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Retiradas:
                                {' '}
                                {formatCurrencyInCents(reconciliationData.withdrawalTotal)}
                                {reconciliationData.withdrawalCount > 0
                                    ? ` (${reconciliationData.withdrawalCount})`
                                    : ''}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <AccountBalance
                                    color={reconciliationData.balance >= 0 ? 'success' : 'error'}
                                    sx={{ mr: 1 }}
                                />
                                <Typography
                                    variant="h6"
                                    color={reconciliationData.balance >= 0 ? 'success.main' : 'error.main'}
                                >
                                    Saldo
                                </Typography>
                            </Box>
                            <Typography
                                variant="h4"
                                color={reconciliationData.balance >= 0 ? 'success.main' : 'error.main'}
                                sx={{ fontWeight: 'bold' }}
                            >
                                {formatCurrencyInCents(reconciliationData.balance)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {reconciliationData.balance >= 0 ? 'Positivo' : 'Negativo'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="h6" color="primary.main">
                                    Resumo
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                {reconciliationData.transactionCount}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total de transações
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Média:
                                {' '}
                                {formatCurrencyInCents(reconciliationData.averageTransaction)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Movimentação por dia
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Valores diários no período filtrado
                    {periodLabel ? ` (${periodLabel})` : ''}
                </Typography>
                <Box sx={{ height: { xs: 280, md: 360 }, position: 'relative' }}>
                    {dailySeries.length > 0 ? (
                        <Line data={dailyChartData} options={dailyChartOptions} />
                    ) : (
                        <Box
                            sx={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Sem dados diários para o filtro atual.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Desconto/Taxas</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Valor Líquido</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Método de Pagamento</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Autorização</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Origem</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '120px' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transactions.length > 0 ? transactions.map((transaction) => (
                            <TableRow key={transaction.id}>
                                <TableCell>{formatDate(transaction.created_at)}</TableCell>
                                <TableCell>
                                    <Typography
                                        color={getTransactionTypeColor(transaction.type)}
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {getTransactionTypeLabel(transaction.type)}
                                        {transaction.type === 'outcome' && getOutcomeCategoryLabel(transaction.outcome_category)
                                            ? ` · ${getOutcomeCategoryLabel(transaction.outcome_category)}`
                                            : ''}
                                    </Typography>
                                </TableCell>
                                <TableCell>{formatCurrencyInCents(transaction.amount)}</TableCell>
                                <TableCell>{formatCurrencyInCents(transaction.deduction)}</TableCell>
                                <TableCell>
                                    <Typography
                                        color={getTransactionTypeColor(transaction.type)}
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {formatCurrencyInCents(transaction.net_amount)}
                                    </Typography>
                                </TableCell>
                                <TableCell>{getPaymentMethodLabel(transaction.payment_method)}</TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                        {applyAuthCode(transaction.authorization_id) || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>{transaction.source || '—'}</TableCell>
                                <TableCell>
                                    {transaction.order_id ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {transaction.description && (
                                                <Typography variant="body2">{transaction.description}</Typography>
                                            )}
                                            <Link
                                                component="button"
                                                variant="body2"
                                                onClick={() => {
                                                    const orderId = formatUuid(transaction.order_id);
                                                    navigate(`/admin/orders/list?search=${orderId}`);
                                                }}
                                                sx={{
                                                    color: 'primary.main',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    fontSize: '0.875rem',
                                                    '&:hover': {
                                                        textDecoration: 'underline',
                                                    },
                                                }}
                                            >
                                                referente ao pedido
                                                {' '}
                                                {presentUuid(transaction.order_id)}
                                            </Link>
                                        </Box>
                                    ) : (
                                        transaction.description || '-'
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Tooltip title="Editar">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleEditTransaction(transaction.id)}
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Excluir">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteTransaction(transaction.id)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={10} align="center">
                                    <Typography variant="body1" color="text.secondary">
                                        Nenhuma transação encontrada
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    mt: 3,
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        setItemsPerPage(event.target.value);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {[...LIST_PAGE_SIZE_OPTIONS].map((size) => (
                        <MenuItem key={size} value={size}>
                            {size}
                            {' '}
                            por página
                        </MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>
        </div>
    );
}

export default TransactionsPage;
